import httpDao from "@/infrastructure/httpDao";

export const ORGANIZATION_LOGO_MAX_BYTES = 2 * 1024 * 1024;
export const ORGANIZATION_LOGO_CONTENT_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
] as const;

export type OrganizationLogoContentType =
  (typeof ORGANIZATION_LOGO_CONTENT_TYPES)[number];

export type OrganizationBranding = {
  id: string;
  name: string;
  legalName: string | null;
  logoUrl: string | null;
  iconUrl: string | null;
  horizontalLogoUrl: string | null;
  postalCode: string | null;
  prefecture: string | null;
  city: string | null;
  address: string | null;
  building: string | null;
  phoneNumber: string | null;
  contactEmail: string | null;
};

export type OrganizationBrandAssetType = "icon" | "horizontal-logo";

export type UpdateOrganizationProfile = {
  name: string;
  legalName: string | null;
  postalCode: string | null;
  prefecture: string | null;
  city: string | null;
  address: string | null;
  building: string | null;
  phoneNumber: string | null;
  contactEmail: string | null;
};

export type OrganizationLogoUpload = {
  uploadUrl: string;
  key: string;
  publicUrl: string | null;
  expiresInSeconds: number;
  maxFileSizeBytes: number;
  requiredHeaders: Record<string, string>;
};

export const organizationBrandingRepository = {
  findCurrent: async (): Promise<OrganizationBranding> => {
    const response = await httpDao.get<OrganizationBranding>(
      "/organization/branding"
    );
    return response.data;
  },

  updateProfile: async (
    profile: UpdateOrganizationProfile
  ): Promise<OrganizationBranding> => {
    const response = await httpDao.put<OrganizationBranding>(
      "/organization/branding",
      profile
    );
    return response.data;
  },

  createLogoUploadUrl: async (file: File): Promise<OrganizationLogoUpload> => {
    const response = await httpDao.post<OrganizationLogoUpload>(
      "/organization/branding/logo-upload-url",
      {
        fileName: file.name,
        contentType: file.type,
        fileSize: file.size,
      }
    );
    return response.data;
  },

  uploadLogo: async (file: File): Promise<OrganizationLogoUpload> => {
    const upload =
      await organizationBrandingRepository.createLogoUploadUrl(file);
    const response = await fetch(upload.uploadUrl, {
      method: "PUT",
      headers: upload.requiredHeaders,
      body: file,
    });

    if (!response.ok) {
      throw new Error("組織ロゴのアップロードに失敗しました");
    }

    return upload;
  },

  saveLogo: async (key: string): Promise<OrganizationBranding> => {
    const response = await httpDao.put<OrganizationBranding>(
      "/organization/branding/logo",
      { key }
    );
    return response.data;
  },

  deleteLogo: async (): Promise<OrganizationBranding> => {
    const response = await httpDao.delete<OrganizationBranding>(
      "/organization/branding/logo"
    );
    return response.data;
  },

  createBrandAssetUploadUrl: async (
    assetType: OrganizationBrandAssetType,
    file: File
  ): Promise<OrganizationLogoUpload> => {
    const response = await httpDao.post<OrganizationLogoUpload>(
      `/organization/branding/brand-assets/${assetType}/upload-url`,
      {
        fileName: file.name,
        contentType: file.type,
        fileSize: file.size,
      }
    );
    return response.data;
  },

  uploadBrandAsset: async (
    assetType: OrganizationBrandAssetType,
    file: File
  ): Promise<OrganizationLogoUpload> => {
    const upload =
      await organizationBrandingRepository.createBrandAssetUploadUrl(
        assetType,
        file
      );
    const response = await fetch(upload.uploadUrl, {
      method: "PUT",
      headers: upload.requiredHeaders,
      body: file,
    });
    if (!response.ok) {
      throw new Error("組織ブランド画像のアップロードに失敗しました");
    }
    return upload;
  },

  saveBrandAsset: async (
    assetType: OrganizationBrandAssetType,
    key: string
  ): Promise<OrganizationBranding> => {
    const response = await httpDao.put<OrganizationBranding>(
      `/organization/branding/brand-assets/${assetType}`,
      { key }
    );
    return response.data;
  },

  deleteBrandAsset: async (
    assetType: OrganizationBrandAssetType
  ): Promise<OrganizationBranding> => {
    const response = await httpDao.delete<OrganizationBranding>(
      `/organization/branding/brand-assets/${assetType}`
    );
    return response.data;
  },
};
